'use client';
import { exploreProperties, PropertyListing } from '@/app/actions/Properties/actions';
import { toast } from '@/components/ui/use-toast';
import { useDebounce } from '@/hooks/useDebounce';
import useSearch from '@/hooks/useSearch';
import { convertToEGP } from '@/lib/currency';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loading } from '../Properties/Loading';
import NoPropertiesFound from '../Properties/NoPropertiesFound';
import PropertyCard from '../Properties/PropertyCard/PropertyCard';
import ListPagination from '../Utilities/ListPagination';
import { FaWhatsapp } from 'react-icons/fa';
import posthog from 'posthog-js';

interface IExplorePropertiesProps {
  initialProperties: PropertyListing[];
  initialTotalPages: number;
  initialCurrentPage: number;
  initialTotalCount: number;
  pageSize: number;
}

export default function ExploreProperties({
  initialProperties,
  initialTotalPages,
  initialCurrentPage,
  initialTotalCount,
  pageSize,
}: IExplorePropertiesProps) {
  const [properties, setProperties] = useState<PropertyListing[]>(initialProperties);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [totalCount, setTotalCount] = useState(initialTotalCount);
  const [isLoading, setIsLoading] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const { state, dispatch } = useSearch();
  const isInitialMount = useRef(true);
  const previousState = useRef(state);
  const previousPage = useRef(state.page);

  useEffect(() => {
    const timeout = setTimeout(() => {
      toast({
        title: "Didn't find what you were looking for?",
        duration: 1000000,
        description: (
          <Link href="https://wa.me/201021514433" target="_blank">
            <div className="flex gap-2">
              <p>
                We&apos;re here to help, <span className="underline">reach out to us on</span>
              </p>
              <div>
                <FaWhatsapp className="w-4 h-4" />
              </div>
            </div>
          </Link>
        ),
      });
    }, 120000);

    return () => clearTimeout(timeout);
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    dispatch({ type: 'SET_PAGE', payload: page });
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const fetchProperties = async function () {
    try {
      setIsLoading(true);
      setProperties([]);
      setTotalPages(0);
      setCurrentPage(0);
      setTotalCount(0);

      // Convert user currency to EGP for backend
      const minPriceInEGP =
        state.minPrice > 0 ? convertToEGP(state.minPrice, state.currency) : undefined;
      const maxPriceInEGP = state.maxPrice
        ? convertToEGP(state.maxPrice, state.currency)
        : undefined;

      posthog.capture('Products Searched', {
        query: {
          destination: state.destination,
          area: state.area,
          minPrice: state.minPrice,
          maxPrice: state.maxPrice,
          minBeds: state.minBeds,
          minBaths: state.minBaths,
          guests: state.guests,
          perks: state.perks,
          dateRange: state.dateRange,
          sort: state.sort,
          currency: state.currency,
          page: state.page,
          pageSize: pageSize,
        },
      });

      const {
        properties,
        totalPages,
        totalCount,
        currentPage: newCurrentPage,
      } = await exploreProperties({
        page: state.page,
        pageSize: pageSize,
        destination: state.destination || undefined,
        area: state.area || undefined,
        minPrice: minPriceInEGP,
        maxPrice: maxPriceInEGP,
        minBeds: state.minBeds,
        minBaths: state.minBaths,
        guests: state.guests,
        perks: state.perks.join(','),
        from: state.dateRange.from || undefined,
        to: state.dateRange.to || undefined,
        sort: state.sort as 'trending' | 'newest' | 'updated' | undefined,
      });

      setProperties(properties);
      setTotalPages(totalPages);
      setCurrentPage(newCurrentPage);
      setTotalCount(totalCount);

      posthog.capture('Product List Viewed', {
        list_id: 'explore',
        category: 'properties',
        products: properties.map((property: PropertyListing) => ({
          product_id: property.id,
          sku: property.id,
          name: property.title,
          price: property.price,
          position: 1,
          category: 'properties',
          url: `https://kawkb.com/properties/${property.id}`,
          image_url: property.images[0],
        })),
      });
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error fetching properties',
        description: 'Please try again later',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsDebouncing(false);
      console.log('properties fetched');
    }
  };

  const debouncedUpdateProperties = useDebounce(() => {
    setIsDebouncing(false);
    fetchProperties();
  }, 500);

  const triggerDebouncedUpdate = () => {
    setIsDebouncing(true);
    debouncedUpdateProperties();
  };

  const memoizedUpdateProperties = useCallback(fetchProperties, [
    state.page,
    pageSize,
    state.destination,
    state.area,
    state.minPrice,
    state.maxPrice,
    state.minBeds,
    state.minBaths,
    state.guests,
    state.perks,
    state.dateRange.from,
    state.dateRange.to,
    state.sort,
  ]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousState.current = state;
      previousPage.current = state.page;
      return;
    }

    const hasStateChanged = JSON.stringify(previousState.current) !== JSON.stringify(state);
    if (hasStateChanged) {
      previousState.current = state;

      if (state.page !== previousPage.current) {
        previousPage.current = state.page;
        memoizedUpdateProperties();
      } else {
        triggerDebouncedUpdate();
      }
    }
  }, [state, triggerDebouncedUpdate, memoizedUpdateProperties]);

  return (
    <div className="space-y-2">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div className="flex flex-col md:flex-row  md:items-center gap-2 w-full">
          <h3 className="text-lg font-milligram w-full">
            {isLoading
              ? 'Looking for properties...'
              : isDebouncing
                ? 'Updating search...'
                : totalCount > 0
                  ? ``
                  : ''}
          </h3>
        </div>
      </div>

      {isLoading || isDebouncing ? (
        <Loading />
      ) : properties.length > 0 ? (
        <div className="md:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <PropertyCard property={property} featured={true} key={property.id} />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full py-16">
          <NoPropertiesFound />
        </div>
      )}

      {totalCount > 0 && (
        <ListPagination
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        />
      )}
    </div>
  );
}
